import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import sharepointService from '../services/sharepointService';
import bedrockService from '../services/bedrockService';
import { formatBytes } from '../utils/formatUtils';
import BackfillFilters from './BackfillFilters';

/**
 * localStorage key prefix for persisting backfill review progress.
 * Keyed per job so multiple jobs don't interfere.
 */
const BACKFILL_DECISIONS_KEY = 'backfill_decisions_';
const BACKFILL_SELECTIONS_KEY = 'backfill_selections_';

/**
 * Save file decisions to localStorage for the given job.
 * @param {string} jobId - Current backfill job ID
 * @param {Object} decisions - fileId -> decision map
 */
const saveDecisionsToStorage = (jobId, decisions) => {
    if (!jobId) return;
    try {
        localStorage.setItem(
            BACKFILL_DECISIONS_KEY + jobId,
            JSON.stringify(decisions)
        );
    } catch (err) {
        console.warn('Failed to save backfill decisions:', err);
    }
};

/**
 * Save review selection IDs to localStorage for the given job.
 * @param {string} jobId - Current backfill job ID
 * @param {Set<string>} selectionSet - Set of selected file IDs
 */
const saveSelectionsToStorage = (jobId, selectionSet) => {
    if (!jobId) return;
    try {
        localStorage.setItem(
            BACKFILL_SELECTIONS_KEY + jobId,
            JSON.stringify([...selectionSet])
        );
    } catch (err) {
        console.warn('Failed to save backfill selections:', err);
    }
};

/**
 * Load saved file decisions from localStorage for the given job.
 * @param {string} jobId - Current backfill job ID
 * @returns {Object|null} Saved decisions or null
 */
const loadDecisionsFromStorage = (jobId) => {
    if (!jobId) return null;
    try {
        const raw = localStorage.getItem(
            BACKFILL_DECISIONS_KEY + jobId
        );
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

/**
 * Load saved review selection IDs from localStorage for the given job.
 * @param {string} jobId - Current backfill job ID
 * @returns {Set<string>|null} Saved selections or null
 */
const loadSelectionsFromStorage = (jobId) => {
    if (!jobId) return null;
    try {
        const raw = localStorage.getItem(
            BACKFILL_SELECTIONS_KEY + jobId
        );
        return raw ? new Set(JSON.parse(raw)) : null;
    } catch {
        return null;
    }
};

/**
 * Remove saved progress from localStorage for the given job.
 * @param {string} jobId - Current backfill job ID
 */
const clearSavedProgress = (jobId) => {
    if (!jobId) return;
    localStorage.removeItem(BACKFILL_DECISIONS_KEY + jobId);
    localStorage.removeItem(BACKFILL_SELECTIONS_KEY + jobId);
};

/**
 * Modal component for backfilling/reorganizing existing SharePoint files.
 * 
 * This component provides a workflow to:
 * 1. Scan a client's SharePoint folder for existing files
 * 2. Select files to reorganize
 * 3. Analyze selected files for suggestions (new name, folder, summary)
 * 4. Review and apply changes (rename, move, add to knowledgebase)
 * 5. Optionally delete files marked as useless
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {string} clientName - The client name to scan files for
 * @param {string} knowledgebaseClientId - The knowledgebase client ID
 * @param {function} onComplete - Callback when backfill is complete
 */
export default function BackfillModal({
    isOpen,
    onClose,
    clientName,
    knowledgebaseClientId,
    onComplete
}) {
    // Steps: 'scan', 'select', 'analyze', 'review', 'apply'
    const [step, setStep] = useState('scan');

    // Scanning state
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState('');
    const [scannedFiles, setScannedFiles] = useState([]);

    // Selection state
    const [selectedFileIds, setSelectedFileIds] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);

    // Analysis state
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisJobs, setAnalysisJobs] = useState({}); // fileId -> { jobId, status, result }
    const [analysisComplete, setAnalysisComplete] = useState(false);
    const [currentJobId, setCurrentJobId] = useState(null); // State machine job ID
    const pollingRef = useRef(null);
    const saveDecisionsTimerRef = useRef(null);

    // Folder options
    const [folderOptions, setFolderOptions] = useState([]);

    // Review/Apply state
    const [fileDecisions, setFileDecisions] = useState({}); // fileId -> { action: 'move'|'delete'|'skip', newName, targetFolder, ... }
    const [reviewSelectedIds, setReviewSelectedIds] = useState(new Set()); // fileIds selected for apply
    const [applying, setApplying] = useState(false);
    const [applyProgress, setApplyProgress] = useState({ current: 0, total: 0 });
    const [applyResults, setApplyResults] = useState([]);

    // Search/filter for scanned files
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        extensions: new Set(),
        paths: new Set(),
        nameFilter: '',
        kbFilter: 'all'
    });

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ column: 'name', direction: 'asc' });

    // Existing job detection
    const [existingJobs, setExistingJobs] = useState([]);
    const [completedJobs, setCompletedJobs] = useState([]);
    const [showCompletedJobs, setShowCompletedJobs] = useState(false);
    const [loadingExistingJobs, setLoadingExistingJobs] = useState(false);
    const [cancellingJobId, setCancellingJobId] = useState(null);

    // Doc types data from API (contains embedded sub_types)
    const [docTypesData, setDocTypesData] = useState([]);

    // Load folder options and doc types on mount
    useEffect(() => {
        if (isOpen && clientName) {
            loadFolderOptions();
            loadDocTypesData();
        }
    }, [isOpen, clientName]);

    /**
     * Load doc types from API (sub_types are embedded in each doc_type)
     */
    const loadDocTypesData = async () => {
        try {
            const resp = await bedrockService.getDocTypes();
            if (resp?.doc_types?.length > 0) {
                setDocTypesData(resp.doc_types);
            }
        } catch (error) {
            console.warn('Failed to load doc types:', error);
        }
    };

    /**
     * Get filtered sub_types based on selected doc_type name.
     * Returns sub_types array from the matching doc_type object.
     */
    const getFilteredSubTypes = useCallback((selectedDocTypeId) => {
        if (!docTypesData || docTypesData.length === 0) {
            return [];
        }
        
        if (!selectedDocTypeId) {
            // No doc_type selected, return all sub_types from all doc_types
            const allSubs = [];
            docTypesData.forEach(dt => {
                if (dt.sub_types && Array.isArray(dt.sub_types)) {
                    dt.sub_types.forEach(st => {
                        const name = typeof st === 'object' ? st.name : st;
                        if (name && !allSubs.find(s => s.name === name)) {
                            allSubs.push({ name, id: st.id || name });
                        }
                    });
                }
            });
            return allSubs;
        }
        
        // Find the doc_type object that matches the selected ID or name (for backward compatibility)
        const docTypeObj = docTypesData.find(dt => dt.id === selectedDocTypeId || dt.name === selectedDocTypeId);
        if (docTypeObj && docTypeObj.sub_types && docTypeObj.sub_types.length > 0) {
            return docTypeObj.sub_types
                .filter(st => typeof st === 'object' && st.name)
                .map(st => ({ name: st.name, id: st.id || st.name }));
        }
        
        return [];
    }, [docTypesData]);

    /**
     * Resolve a doc_type_id (which could be a UUID or a name like "Mixed") to a display name.
     * Returns the name if found, or the original value as fallback.
     */
    const resolveDocTypeName = useCallback((docTypeId) => {
        if (!docTypeId || !docTypesData || docTypesData.length === 0) {
            return docTypeId || '';
        }
        // First try to find by ID
        const byId = docTypesData.find(dt => dt.id === docTypeId);
        if (byId) return byId.name;
        // Then try to find by name (for values like "Mixed")
        const byName = docTypesData.find(dt => dt.name === docTypeId);
        if (byName) return byName.name;
        // Return original value as fallback
        return docTypeId;
    }, [docTypesData]);

    /**
     * Resolve a sub_type_id (which could be a UUID or a name like "Mixed") to a display name.
     * Searches across all doc types' sub_types.
     */
    const resolveSubTypeName = useCallback((subTypeId) => {
        if (!subTypeId || !docTypesData || docTypesData.length === 0) {
            return subTypeId || '';
        }
        // Search through all doc types for matching sub_type
        for (const dt of docTypesData) {
            if (dt.sub_types && Array.isArray(dt.sub_types)) {
                // Try by ID
                const byId = dt.sub_types.find(st => st.id === subTypeId);
                if (byId) return byId.name;
                // Try by name
                const byName = dt.sub_types.find(st => st.name === subTypeId);
                if (byName) return byName.name;
            }
        }
        // Return original value as fallback
        return subTypeId;
    }, [docTypesData]);

    // Load folder options on mount
    useEffect(() => {
        if (isOpen && clientName) {
            loadFolderOptions();
        }
    }, [isOpen, clientName]);

    // Reload folder options with the correct base path once files are scanned
    useEffect(() => {
        if (scannedFiles.length > 0) {
            const basePath = getClientBasePath();
            if (basePath) {
                loadFolderOptions(basePath);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scannedFiles]);

    // Check for existing jobs when modal opens
    useEffect(() => {
        if (isOpen && clientName) {
            checkExistingJobs();
        }
    }, [isOpen, clientName]);

    /**
     * Check for existing in-progress jobs for this client.
     */
    const checkExistingJobs = async () => {
        setLoadingExistingJobs(true);
        try {
            const response = await sharepointService.listBackfillJobs(clientName);
            const allJobs = response.jobs || [];

            // Show pending, processing, or analyzed jobs (analyzed = needs user review)
            const activeJobs = allJobs.filter(
                job => job.status === 'pending' || job.status === 'processing' || job.status === 'analyzed'
            );
            setExistingJobs(activeJobs);

            // Also track completed jobs so user knows what's already been run
            const finishedJobs = allJobs.filter(
                job => job.status === 'completed' || job.status === 'applied'
            ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Most recent first
            setCompletedJobs(finishedJobs);
        } catch (error) {
            console.error('Error checking existing jobs:', error);
            setExistingJobs([]);
            setCompletedJobs([]);
        } finally {
            setLoadingExistingJobs(false);
        }
    };

    /**
     * Resume an existing job.
     */
    const resumeJob = async (jobId) => {
        try {
            setAnalyzing(true);
            setStep('analyze');
            setCurrentJobId(jobId);

            // Get initial job status to populate files
            const jobStatus = await sharepointService.getBackfillJobStatus(jobId);

            // Populate scanned files from job results
            if (jobStatus.results && Array.isArray(jobStatus.results)) {
                const files = jobStatus.results.map(r => ({
                    fileId: r.fileId,
                    name: r.fileName,
                    path: r.filePath,
                    // Extract folder path from file path (remove filename)
                    folderPath: r.filePath ? r.filePath.split('/').slice(0, -1).join('/') : ''
                }));
                setScannedFiles(files);
                setSelectedFileIds(new Set(files.map(f => f.fileId)));

                // Set up jobs state
                const jobs = {};
                for (const result of jobStatus.results) {
                    jobs[result.fileId] = {
                        status: result.status || 'pending',
                        fileName: result.fileName,
                        s3Key: result.s3Key || null,
                        result: result.status === 'completed' ? {
                            originalName: result.fileName,
                            suggestedName: result.suggestedFilename,
                            suggestedFolder: result.suggestedFolder,
                            summary: result.summary,
                            category: result.category,
                            docTypeId: result.docTypeId || '',
                            subTypeId: result.subTypeId || '',
                            accountNumber: result.accountNumber || '',
                            userAction: result.userAction || null,
                            userNewName: result.userNewName || null,
                            userTargetFolder: result.userTargetFolder || null,
                            userDocTypeId: result.userDocTypeId || null,
                            userSubTypeId: result.userSubTypeId || null,
                            userAccountNumber: result.userAccountNumber || null,
                        } : null,
                        error: result.error
                    };
                }
                setAnalysisJobs(jobs);
            }

            // Check if job is already complete (analyzed = ready for user review)
            if (jobStatus.status === 'completed' || jobStatus.status === 'analyzed') {
                setAnalyzing(false);
                setAnalysisComplete(true);
                // Prepare decisions
                const jobs = {};
                for (const result of jobStatus.results) {
                    jobs[result.fileId] = {
                        status: 'completed',
                        fileName: result.fileName,
                        result: {
                            originalName: result.fileName,
                            suggestedName: result.suggestedFilename,
                            suggestedFolder: result.suggestedFolder,
                            summary: result.summary,
                            category: result.category,
                            docTypeId: result.docTypeId || '',
                            subTypeId: result.subTypeId || '',
                            accountNumber: result.accountNumber || '',
                            userAction: result.userAction || null,
                            userNewName: result.userNewName || null,
                            userTargetFolder: result.userTargetFolder || null,
                            userDocTypeId: result.userDocTypeId || null,
                            userSubTypeId: result.userSubTypeId || null,
                            userAccountNumber: result.userAccountNumber || null,
                        }
                    };
                }
                initializeDecisions(jobs);
            } else if (jobStatus.status === 'failed') {
                setAnalyzing(false);
                setScanError(jobStatus.error || 'Job failed');
            } else {
                // Still in progress, start polling
                startPolling(jobId);
            }
        } catch (error) {
            console.error('Error resuming job:', error);
            setScanError(error.message || 'Failed to resume job');
            setAnalyzing(false);
        }
    };

    /**
     * Cancel and delete an existing job.
     * This stops the state machine execution and removes the job from DynamoDB.
     */
    const cancelJob = async (jobId) => {
        if (!window.confirm('Are you sure you want to cancel and delete this job? This cannot be undone.')) {
            return;
        }

        try {
            setCancellingJobId(jobId);
            await sharepointService.cancelBackfillJob(jobId);

            // Remove the cancelled job from the existing jobs list
            setExistingJobs(prev => prev.filter(job => job.job_id !== jobId));

            // If this was the current job being analyzed, reset the state
            if (currentJobId === jobId) {
                setCurrentJobId(null);
                setAnalyzing(false);
                setStep('scan');
            }
        } catch (error) {
            console.error('Error cancelling job:', error);
            alert('Failed to cancel job: ' + (error.response?.data?.detail || error.message));
        } finally {
            setCancellingJobId(null);
        }
    };

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep('scan');
            setScanning(false);
            setScanError('');
            setScannedFiles([]);
            setSelectedFileIds(new Set());
            setSelectAll(false);
            setAnalyzing(false);
            setAnalysisJobs({});
            setAnalysisComplete(false);
            setCurrentJobId(null);
            setFileDecisions({});
            setApplying(false);
            setApplyProgress({ current: 0, total: 0 });
            setApplyResults([]);
            setSearchTerm('');
            setFilters({
                extensions: new Set(),
                paths: new Set(),
                nameFilter: '',
                kbFilter: 'all'
            });
            setSortConfig({ column: 'name', direction: 'asc' });
            setExistingJobs([]);
            setCompletedJobs([]);
            setShowCompletedJobs(false);
        }
    }, [isOpen]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, []);

    // Auto-save file decisions to localStorage on every change
    useEffect(() => {
        if (currentJobId && Object.keys(fileDecisions).length > 0) {
            saveDecisionsToStorage(currentJobId, fileDecisions);
        }
    }, [currentJobId, fileDecisions]);

    // Auto-save review selections to localStorage on every change
    useEffect(() => {
        if (currentJobId && reviewSelectedIds.size > 0) {
            saveSelectionsToStorage(currentJobId, reviewSelectedIds);
        }
    }, [currentJobId, reviewSelectedIds]);

    // Debounce-save file decisions to DynamoDB (2s after last edit)
    useEffect(() => {
        if (!currentJobId || Object.keys(fileDecisions).length === 0) {
            return;
        }

        // Clear any pending save
        if (saveDecisionsTimerRef.current) {
            clearTimeout(saveDecisionsTimerRef.current);
        }

        saveDecisionsTimerRef.current = setTimeout(() => {
            sharepointService
                .saveBackfillDecisions(currentJobId, fileDecisions)
                .then(() => {
                    console.debug('Backfill decisions saved to DynamoDB');
                })
                .catch((err) => {
                    console.warn('Failed to save decisions to DynamoDB:', err);
                });
        }, 2000);

        return () => {
            if (saveDecisionsTimerRef.current) {
                clearTimeout(saveDecisionsTimerRef.current);
            }
        };
    }, [currentJobId, fileDecisions]);

    /**
     * Derive the client root folder path (first 3 segments) from scanned files.
     * e.g., "Client Documents/Clients R - Z/Schmitz, Greg"
     */
    const getClientBasePath = () => {
        if (scannedFiles.length > 0) {
            const samplePath = (scannedFiles[0].folderPath || scannedFiles[0].path || '').replace(/\\/g, '/');
            const parts = samplePath.split('/');
            if (parts.length >= 3) {
                return parts.slice(0, 3).join('/');
            }
        }
        return null;
    };

    /**
     * Load folder options: predefined first, then existing SharePoint folders.
     */
    const loadFolderOptions = async (knownBasePath = null) => {
        try {
            const basePath = knownBasePath || getClientBasePath();
            const [predefinedResponse, existingResponse] = await Promise.all([
                sharepointService.getBackfillFolderOptions(),
                clientName ? sharepointService.getClientFolders(clientName, basePath) : Promise.resolve({ folders: [] })
            ]);

            const predefinedFolders = predefinedResponse.folders || [];
            const rawExistingFolders = existingResponse.folders || [];

            // Determine client base path from the first existing folder (first 3 path segments)
            // e.g., "Client Documents/Clients R - Z/Test, Me"
            let clientBasePath = '';
            if (rawExistingFolders.length > 0) {
                const samplePath = (rawExistingFolders[0].full_path || '').replace(/\\/g, '/');
                const parts = samplePath.split('/');
                if (parts.length >= 3) {
                    clientBasePath = parts.slice(0, 3).join('/');
                }
            }

            // Extract relative subfolder path (strip client base prefix)
            const getRelPath = (full) => {
                const normalized = (full || '').replace(/\\/g, '/');
                if (clientBasePath && normalized.startsWith(clientBasePath + '/')) {
                    return normalized.substring(clientBasePath.length + 1);
                }
                if (clientBasePath && normalized === clientBasePath) {
                    return '/';
                }
                return normalized;
            };

            // Build the full predefined section (always shown first, no match/promotion needed)
            const predefinedSection = predefinedFolders
                .filter(pf => pf.path !== '/' && pf.id !== 'root')
                .map(pf => ({
                    id: `predefined_${pf.id}`,
                    name: pf.name,
                    description: pf.description
                }));

            // Build a set of predefined names for deduplication (case-insensitive)
            const predefinedNameSet = new Set(predefinedSection.map(pf => pf.name.toLowerCase()));

            // Existing folders whose names aren't covered by a predefined entry
            const otherExisting = rawExistingFolders
                .filter(f => {
                    const rel = getRelPath(f.full_path || f.path || f.name || '');
                    return !predefinedNameSet.has(rel.toLowerCase()) && !predefinedNameSet.has((f.name || '').toLowerCase());
                })
                .map(f => ({
                    id: f.id || `existing_${f.name}`,
                    name: getRelPath(f.full_path || f.path || f.name || '')
                }));

            otherExisting.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));

            const allOptions = [
                ...predefinedSection,
                ...(predefinedSection.length > 0 && otherExisting.length > 0
                    ? [{ id: 'separator', name: '──────────────', isSeparator: true }]
                    : []),
                ...otherExisting
            ];

            setFolderOptions(allOptions.length > 0 ? allOptions : predefinedFolders);
        } catch (error) {
            console.error('Failed to load folder options:', error);
            // Use default options as fallback
            setFolderOptions([
                { id: 'financial', name: 'Financial Documents' },
                { id: 'legal', name: 'Legal' },
                { id: 'planning', name: 'Financial Planning' },
                { id: 'insurance', name: 'Insurance' },
                { id: 'taxes', name: 'Tax Documents' },
                { id: 'correspondence', name: 'Correspondence' },
                { id: 'other', name: 'Other' }
            ]);
        }
    };

    /**
     * Scan the client's SharePoint folder for existing files.
     */
    const handleScan = async () => {
        if (!clientName) {
            setScanError('Client name is required');
            return;
        }

        setScanning(true);
        setScanError('');

        try {
            // Pass knowledgebaseClientId to check which files are already in KB
            const response = await sharepointService.scanClientFiles(clientName, knowledgebaseClientId);

            if (response.success) {
                setScannedFiles(response.files || []);
                setStep('select');
            } else {
                setScanError(response.error || 'Failed to scan files');
            }
        } catch (error) {
            console.error('Error scanning files:', error);
            setScanError(error.message || 'Failed to scan files');
        } finally {
            setScanning(false);
        }
    };

    /**
     * Toggle selection of a single file.
     */
    const toggleFileSelection = (fileId) => {
        setSelectedFileIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) {
                newSet.delete(fileId);
            } else {
                newSet.add(fileId);
            }
            return newSet;
        });
    };

    /**
     * Toggle selection of all files.
     */
    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectedFileIds(new Set());
        } else {
            setSelectedFileIds(new Set(filteredFiles.map(f => f.fileId)));
        }
        setSelectAll(!selectAll);
    };

    /**
     * Get filtered files based on search term and filters.
     */
    const filteredFiles = useMemo(() => {
        return scannedFiles.filter(file => {
            // Legacy search term filter (for backward compatibility)
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                if (!file.name.toLowerCase().includes(term) && !file.path.toLowerCase().includes(term)) {
                    return false;
                }
            }

            // Name filter
            if (filters.nameFilter) {
                const nameFilterLower = filters.nameFilter.toLowerCase();
                if (!file.name.toLowerCase().includes(nameFilterLower)) {
                    return false;
                }
            }

            // Path filter (multi-select) - includes subfolders
            if (filters.paths.size > 0) {
                const filePath = file.folderPath || file.path || '';
                // Check if file's path matches any selected path or is a subfolder of it
                let matchesPath = false;
                for (const selectedPath of filters.paths) {
                    if (filePath === selectedPath || filePath.startsWith(selectedPath + '/')) {
                        matchesPath = true;
                        break;
                    }
                }
                if (!matchesPath) {
                    return false;
                }
            }

            // Extension filter
            if (filters.extensions.size > 0) {
                const fileName = file.name || '';
                const lastDot = fileName.lastIndexOf('.');
                if (lastDot > 0) {
                    const ext = fileName.substring(lastDot + 1).toLowerCase();
                    if (!filters.extensions.has(ext)) {
                        return false;
                    }
                } else {
                    // No extension, don't match if extension filter is active
                    return false;
                }
            }

            // Knowledgebase filter
            if (filters.kbFilter && filters.kbFilter !== 'all') {
                if (filters.kbFilter === 'in' && !file.inKnowledgebase) {
                    return false;
                }
                if (filters.kbFilter === 'not' && file.inKnowledgebase) {
                    return false;
                }
            }

            return true;
        });
    }, [scannedFiles, searchTerm, filters]);

    /**
     * Compute common path prefix for display purposes (stops one level before deepest common folder to show client name).
     */
    const commonPathPrefix = useMemo(() => {
        const paths = scannedFiles.map(f => f.folderPath || f.path || '').filter(p => p);
        if (paths.length === 0) return '';

        // Find the deepest common prefix among all paths
        let prefix = paths[0];
        for (let i = 1; i < paths.length; i++) {
            while (!paths[i].startsWith(prefix) && prefix.length > 0) {
                const lastSlash = prefix.lastIndexOf('/');
                prefix = lastSlash > 0 ? prefix.substring(0, lastSlash) : '';
            }
            if (prefix.length === 0) break;
        }

        // Go up one more level so the client folder name is always shown
        if (prefix) {
            const lastSlash = prefix.lastIndexOf('/');
            if (lastSlash > 0) {
                prefix = prefix.substring(0, lastSlash);
            }
        }

        return prefix;
    }, [scannedFiles]);

    /**
     * Format a path for display by replacing the common prefix with "...".
     */
    const formatPathForDisplay = (path) => {
        if (!path) return '';
        if (commonPathPrefix && path.startsWith(commonPathPrefix)) {
            let remainder = path.substring(commonPathPrefix.length);
            if (remainder.startsWith('/')) {
                remainder = remainder.substring(1);
            }
            if (!remainder) {
                // Path equals the prefix parent - show the last folder name
                const trimmedPrefix = commonPathPrefix.endsWith('/')
                    ? commonPathPrefix.slice(0, -1)
                    : commonPathPrefix;
                const lastSlash = trimmedPrefix.lastIndexOf('/');
                return '.../' + (lastSlash >= 0 ? trimmedPrefix.substring(lastSlash + 1) : trimmedPrefix);
            }
            return '.../' + remainder;
        }
        return path.length > 40 ? '...' + path.slice(-40) : path;
    };

    /**
     * Handle column header click to toggle sort.
     */
    const handleSort = (column) => {
        setSortConfig(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    /**
     * Get sorted files based on current sort configuration.
     */
    const sortedFiles = useMemo(() => {
        const sorted = [...filteredFiles];
        const { column, direction } = sortConfig;
        const modifier = direction === 'asc' ? 1 : -1;

        sorted.sort((a, b) => {
            let aVal, bVal;

            switch (column) {
                case 'name':
                    aVal = (a.name || '').toLowerCase();
                    bVal = (b.name || '').toLowerCase();
                    return aVal.localeCompare(bVal) * modifier;
                case 'path':
                    aVal = (a.folderPath || a.path || '').toLowerCase();
                    bVal = (b.folderPath || b.path || '').toLowerCase();
                    return aVal.localeCompare(bVal) * modifier;
                case 'size':
                    aVal = a.size || 0;
                    bVal = b.size || 0;
                    return (aVal - bVal) * modifier;
                case 'modified':
                    aVal = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
                    bVal = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
                    return (aVal - bVal) * modifier;
                case 'kb':
                    aVal = a.inKnowledgebase ? 1 : 0;
                    bVal = b.inKnowledgebase ? 1 : 0;
                    return (aVal - bVal) * modifier;
                default:
                    return 0;
            }
        });

        return sorted;
    }, [filteredFiles, sortConfig]);

    /**
     * Render sort indicator for column header.
     */
    const renderSortIcon = (column) => {
        if (sortConfig.column !== column) {
            return <i className="bi bi-chevron-expand text-muted ms-1" style={{ fontSize: '0.7rem' }}></i>;
        }
        return sortConfig.direction === 'asc'
            ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.7rem' }}></i>
            : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
    };

    /**
     * Start analyzing selected files using the state machine.
     * This starts a single job that processes all files asynchronously.
     */
    const handleAnalyze = async () => {
        const fileIds = Array.from(selectedFileIds);
        if (fileIds.length === 0) {
            return;
        }

        setAnalyzing(true);
        setAnalysisComplete(false);
        setStep('analyze');

        try {
            // Get the selected files' info
            const selectedFiles = scannedFiles.filter(f => selectedFileIds.has(f.fileId));

            // Initialize jobs state - all start as 'pending'
            const initialJobs = {};
            selectedFiles.forEach(file => {
                initialJobs[file.fileId] = {
                    status: 'pending',
                    fileName: file.name,
                    result: null
                };
            });
            setAnalysisJobs(initialJobs);

            // Start the state machine job
            console.log(`Starting backfill job for ${selectedFiles.length} files`);
            const response = await sharepointService.startBackfillJob(clientName, selectedFiles);

            if (response.jobId) {
                setCurrentJobId(response.jobId);
                console.log(`Backfill job started with ID: ${response.jobId}`);

                // Start polling for job status
                startPolling(response.jobId);
            } else {
                throw new Error(response.message || 'Failed to start analysis job');
            }
        } catch (error) {
            console.error('Error starting analysis:', error);
            setAnalyzing(false);
            setScanError(error.message || 'Failed to start analysis');
        }
    };

    /**
     * Start polling for state machine job status.
     * Polls the job status and updates individual file results.
     */
    const startPolling = useCallback((jobId) => {
        // Clear any existing polling
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
        }

        const pollJobStatus = async () => {
            try {
                const jobStatus = await sharepointService.getBackfillJobStatus(jobId);

                console.log(`Job ${jobId} status: ${jobStatus.status}, processed: ${jobStatus.processedFiles}/${jobStatus.totalFiles}`);

                // Update jobs state from job results
                const updatedJobs = {};

                if (jobStatus.results && Array.isArray(jobStatus.results)) {
                    for (const result of jobStatus.results) {
                        updatedJobs[result.fileId] = {
                            status: result.status || 'pending',
                            fileName: result.fileName,
                            s3Key: result.s3Key || null,
                            result: result.status === 'completed' ? {
                                originalName: result.fileName,
                                suggestedName: result.suggestedFilename,
                                suggestedFolder: result.suggestedFolder,
                                customFolderPath: result.customFolderPath,
                                summary: result.summary,
                                category: result.category,
                                docTypeId: result.docTypeId || result.docType || '',
                                subTypeId: result.subTypeId || result.subType || '',
                                accountNumber: result.accountNumber || '',
                                // User-edited decisions saved to DDB
                                userAction: result.userAction || null,
                                userNewName: result.userNewName || null,
                                userTargetFolder: result.userTargetFolder || null,
                                userDocTypeId: result.userDocTypeId || null,
                                userSubTypeId: result.userSubTypeId || null,
                                userAccountNumber: result.userAccountNumber || null,
                            } : null,
                            error: result.error
                        };
                    }
                }

                setAnalysisJobs(updatedJobs);

                // Check if job is complete (analyzed), fully completed, or failed
                if (jobStatus.status === 'completed' || jobStatus.status === 'analyzed' || jobStatus.status === 'failed') {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    setAnalyzing(false);
                    setAnalysisComplete(true);

                    if (jobStatus.status === 'completed' || jobStatus.status === 'analyzed') {
                        // Initialize file decisions based on analysis results
                        initializeDecisions(updatedJobs);
                    } else {
                        setScanError(jobStatus.error || 'Analysis job failed');
                    }
                }
            } catch (error) {
                console.error(`Error polling job ${jobId}:`, error);
                // Don't stop polling on transient errors
            }
        };

        // Initial poll
        pollJobStatus();

        // Then poll every 30 seconds
        pollingRef.current = setInterval(pollJobStatus, 30000);
    }, []);

    /**
     * Initialize file decisions based on analysis results.
     * Restores user edits from three sources (highest priority first):
     *   1. DynamoDB (user_* fields returned in job results)
     *   2. localStorage (same-browser fallback)
     *   3. AI-suggested defaults
     */
    const initializeDecisions = (jobs) => {
        const decisions = {};

        for (const [fileId, job] of Object.entries(jobs)) {
            if (job.status === 'completed' && job.result) {
                const result = job.result;
                // Start with AI-suggested defaults
                decisions[fileId] = {
                    action: 'move',
                    newName: result.suggestedName || '',
                    targetFolder: (result.suggestedFolder || 'Other').replace(/\\/g, '/'),
                    category: result.category || '',
                    summary: result.summary || '',
                    docTypeId: result.docTypeId || '',
                    subTypeId: result.subTypeId || '',
                    accountNumber: result.accountNumber || ''
                };

                // Layer 1: Restore user edits saved in DynamoDB
                if (result.userAction) {
                    decisions[fileId].action = result.userAction;
                }
                if (result.userNewName != null) {
                    decisions[fileId].newName = result.userNewName;
                }
                if (result.userTargetFolder != null) {
                    decisions[fileId].targetFolder = result.userTargetFolder;
                }
                if (result.userDocTypeId != null) {
                    decisions[fileId].docTypeId = result.userDocTypeId;
                }
                if (result.userSubTypeId != null) {
                    decisions[fileId].subTypeId = result.userSubTypeId;
                }
                if (result.userAccountNumber != null) {
                    decisions[fileId].accountNumber = result.userAccountNumber;
                }
            }
        }

        // Layer 2: Merge localStorage edits (may be more recent
        // than the last DDB save if the debounce hadn't fired yet)
        const savedDecisions = loadDecisionsFromStorage(currentJobId);
        if (savedDecisions) {
            for (const [fileId, saved] of Object.entries(savedDecisions)) {
                if (decisions[fileId]) {
                    decisions[fileId] = { ...decisions[fileId], ...saved };
                }
            }
        }

        setFileDecisions(decisions);

        // Restore saved review selections, or default to none
        const savedSelections = loadSelectionsFromStorage(currentJobId);
        if (savedSelections && savedSelections.size > 0) {
            const validSelections = new Set(
                [...savedSelections].filter(id => decisions[id])
            );
            setReviewSelectedIds(validSelections);
        } else {
            setReviewSelectedIds(new Set());
        }
        setStep('review');
    };

    /**
     * Update a decision for a specific file.
     */
    const updateDecision = (fileId, updates) => {
        setFileDecisions(prev => ({
            ...prev,
            [fileId]: { ...prev[fileId], ...updates }
        }));
        // If action is changed to skip, remove from selection
        if (updates.action === 'skip') {
            setReviewSelectedIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(fileId);
                return newSet;
            });
        }
    };

    /**
     * Toggle review selection for a file.
     */
    const toggleReviewSelection = (fileId) => {
        setReviewSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) {
                newSet.delete(fileId);
            } else {
                newSet.add(fileId);
            }
            return newSet;
        });
    };

    /**
     * Select/Deselect all in review step.
     */
    const toggleReviewSelectAll = () => {
        const nonSkippedFileIds = Object.entries(fileDecisions)
            .filter(([_, decision]) => decision.action !== 'skip')
            .map(([fileId]) => fileId);
        
        const allSelected = nonSkippedFileIds.every(id => reviewSelectedIds.has(id));
        
        if (allSelected) {
            setReviewSelectedIds(new Set());
        } else {
            setReviewSelectedIds(new Set(nonSkippedFileIds));
        }
    };

    /**
     * Skip selected files - marks them as skipped in the backend and removes from review.
     */
    const handleSkipSelected = async () => {
        const filesToSkip = [...reviewSelectedIds];
        if (filesToSkip.length === 0) return;

        // Update each file's status in the backend
        for (const fileId of filesToSkip) {
            if (currentJobId) {
                try {
                    await sharepointService.updateBackfillFileStatus(
                        currentJobId,
                        fileId,
                        'skipped'
                    );
                } catch (err) {
                    console.error(`Failed to mark file ${fileId} as skipped:`, err);
                }
            }
        }

        // Remove skipped files from decisions
        const remainingDecisions = { ...fileDecisions };
        for (const fileId of filesToSkip) {
            delete remainingDecisions[fileId];
        }
        setFileDecisions(remainingDecisions);
        setReviewSelectedIds(new Set());

        // Check if all files are now processed
        if (Object.keys(remainingDecisions).length === 0) {
            // All files handled - show completion
            setApplyResults([]);
            setStep('apply');
        }
    };

    /**
     * Apply all decisions.
     */
    const handleApply = async () => {
        const filesToProcess = Object.entries(fileDecisions).filter(
            ([fileId, decision]) => decision.action !== 'skip' && reviewSelectedIds.has(fileId)
        );

        if (filesToProcess.length === 0) {
            return;
        }

        setApplying(true);
        setApplyProgress({ current: 0, total: filesToProcess.length });
        setApplyResults([]);
        setStep('apply');

        const results = [];
        const processedFileIds = []; // Track successfully processed files to remove from decisions

        for (let i = 0; i < filesToProcess.length; i++) {
            const [fileId, decision] = filesToProcess[i];
            const file = scannedFiles.find(f => f.fileId === fileId);

            setApplyProgress({ current: i + 1, total: filesToProcess.length });

            try {
                let fileStatus = 'applied';
                
                if (decision.action === 'delete') {
                    // Delete the file
                    const response = await sharepointService.deleteBackfillFile(
                        clientName,
                        fileId,
                        file?.path || ''
                    );

                    results.push({
                        fileId,
                        fileName: file?.name || 'Unknown',
                        action: 'delete',
                        success: response.success,
                        error: response.error
                    });
                    
                    fileStatus = response.success ? 'deleted' : 'failed';
                } else if (decision.action === 'move') {
                    // Get s3Key from analysisJobs (set during analysis polling)
                    const analysisJob = analysisJobs[fileId];
                    // Move/rename and always add to knowledgebase
                    const response = await sharepointService.applyBackfillChanges({
                        clientName,
                        fileId,
                        currentPath: file?.path || '',
                        newName: decision.newName,
                        targetFolder: decision.targetFolder,
                        addToKnowledgebase: true,
                        knowledgebaseClientId: knowledgebaseClientId,
                        docTypeId: decision.docTypeId,
                        subTypeId: decision.subTypeId,
                        accountNumber: decision.accountNumber,
                        // If file already exists in KB, pass the existing file ID to delete/replace it
                        existingKbFileId: file?.kbFileId || null,
                        // Pass the staging S3 key from backfill analysis to avoid re-downloading from SharePoint
                        originalS3Key: analysisJob?.s3Key || null
                    });

                    results.push({
                        fileId,
                        fileName: file?.name || 'Unknown',
                        action: 'move',
                        success: response.success,
                        newPath: response.newPath,
                        error: response.error
                    });
                    
                    fileStatus = response.success ? 'applied' : 'failed';
                }

                // Update file status in the backend job
                if (currentJobId) {
                    try {
                        await sharepointService.updateBackfillFileStatus(
                            currentJobId,
                            fileId,
                            fileStatus,
                            fileStatus === 'failed' ? results[results.length - 1].error : null
                        );
                    } catch (err) {
                        console.error(`Failed to update file status for ${fileId}:`, err);
                    }
                }

                // Track successfully processed files
                if (fileStatus !== 'failed') {
                    processedFileIds.push(fileId);
                }
            } catch (error) {
                console.error(`Error processing file ${fileId}:`, error);
                results.push({
                    fileId,
                    fileName: file?.name || 'Unknown',
                    action: decision.action,
                    success: false,
                    error: error.message
                });
                
                // Mark as failed in backend
                if (currentJobId) {
                    try {
                        await sharepointService.updateBackfillFileStatus(
                            currentJobId,
                            fileId,
                            'failed',
                            error.message
                        );
                    } catch (err) {
                        console.error(`Failed to update file status for ${fileId}:`, err);
                    }
                }
            }
        }

        setApplyResults(results);
        setApplying(false);

        // Remove processed files from decisions and selection
        const remainingDecisions = { ...fileDecisions };
        for (const fileId of processedFileIds) {
            delete remainingDecisions[fileId];
        }
        setFileDecisions(remainingDecisions);
        setReviewSelectedIds(new Set());

        // Check if there are remaining files to review
        const remainingCount = Object.keys(remainingDecisions).length;
        const successCount = results.filter(r => r.success).length;

        if (remainingCount === 0) {
            // All files processed - job is complete
            console.log(`Job ${currentJobId} completed - all files processed`);

            // Clear saved progress since job is done
            clearSavedProgress(currentJobId);
            
            // Call completion callback
            if (onComplete) {
                onComplete({
                    totalProcessed: results.length,
                    successCount,
                    failureCount: results.length - successCount
                });
            }
        } else {
            // Still have files remaining - stay in apply step to show results,
            // user can click "Continue" to go back to review
            console.log(`${remainingCount} files remaining in job ${currentJobId}`);
        }
    };

    /**
     * Get the original file info for a file ID.
     */
    const getFileInfo = (fileId) => {
        return scannedFiles.find(f => f.fileId === fileId);
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="bi bi-arrow-repeat me-2 text-primary"></i>
                                Backfill & Reorganize Files - {clientName}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                disabled={scanning || applying}
                            ></button>
                        </div>

                        <div className="modal-body">
                            {/* Progress Steps Indicator */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    {['scan', 'select', 'analyze', 'review', 'apply'].map((s, idx) => (
                                        <React.Fragment key={s}>
                                            <div className={`d-flex flex-column align-items-center ${step === s ? 'text-primary' : ''}`}>
                                                <div className={`rounded-circle d-flex align-items-center justify-content-center ${step === s ? 'bg-primary text-white' :
                                                        ['scan', 'select', 'analyze', 'review', 'apply'].indexOf(step) > idx ? 'bg-success text-white' : 'bg-secondary text-white'
                                                    }`} style={{ width: 32, height: 32 }}>
                                                    {['scan', 'select', 'analyze', 'review', 'apply'].indexOf(step) > idx ? (
                                                        <i className="bi bi-check"></i>
                                                    ) : (
                                                        idx + 1
                                                    )}
                                                </div>
                                                <small className="mt-1 text-capitalize">{s}</small>
                                            </div>
                                            {idx < 4 && (
                                                <div className={`flex-grow-1 mx-2 border-top ${['scan', 'select', 'analyze', 'review', 'apply'].indexOf(step) > idx ? 'border-success' : 'border-secondary'
                                                    }`} style={{ height: 2 }}></div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>

                            {/* Step 1: Scan */}
                            {step === 'scan' && (
                                <div className="text-center py-5">
                                    <h4 className="mb-3">
                                        <i className="bi bi-folder-check me-2"></i>
                                        Scan SharePoint Files
                                    </h4>
                                    <p className="text-muted mb-4">
                                        Scan {clientName}'s SharePoint folder to find existing files that can be reorganized.
                                    </p>

                                    {/* Existing Jobs Section */}
                                    {loadingExistingJobs ? (
                                        <div className="mb-4">
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Checking for existing jobs...
                                        </div>
                                    ) : existingJobs.length > 0 && (
                                        <div className="alert alert-info text-start mb-4">
                                            <h6 className="alert-heading">
                                                <i className="bi bi-clock-history me-2"></i>
                                                Existing Analysis Jobs Found
                                            </h6>
                                            <p className="mb-2">
                                                You have {existingJobs.length} in-progress job{existingJobs.length > 1 ? 's' : ''} for this client.
                                            </p>
                                            <div className="list-group">
                                                {existingJobs.map(job => (
                                                    <div key={job.job_id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <strong>{job.total_files} files</strong>
                                                            <span className="text-muted ms-2">
                                                                ({job.completed_files}/{job.total_files} processed)
                                                            </span>
                                                            {job.status === 'analyzed' && (
                                                                <span className="badge bg-success ms-2">Ready for Review</span>
                                                            )}
                                                            {(job.status === 'pending' || job.status === 'processing') && (
                                                                <span className="badge bg-warning text-dark ms-2">In Progress</span>
                                                            )}
                                                            <br />
                                                            <small className="text-muted">
                                                                Started: {new Date(job.created_at).toLocaleString()}
                                                            </small>
                                                        </div>
                                                        <div className="btn-group">
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm ${job.status === 'analyzed' ? 'btn-success' : 'btn-outline-primary'}`}
                                                                onClick={() => resumeJob(job.job_id)}
                                                                disabled={cancellingJobId === job.job_id}
                                                            >
                                                                <i className={`bi ${job.status === 'analyzed' ? 'bi-check2-square' : 'bi-eye-fill'} me-1`}></i>
                                                                {job.status === 'analyzed' ? 'Review' : 'View'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => cancelJob(job.job_id)}
                                                                disabled={cancellingJobId === job.job_id}
                                                                title="Cancel and delete this job"
                                                            >
                                                                {cancellingJobId === job.job_id ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                ) : (
                                                                    <i className="bi bi-x-circle me-1"></i>
                                                                )}
                                                                {cancellingJobId === job.job_id ? '' : 'Cancel'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <hr />
                                            <p className="mb-0 text-muted small">
                                                Or start a new scan below to create a new analysis job.
                                            </p>
                                        </div>
                                    )}

                                    {/* Completed Jobs Section (collapsible) */}
                                    {completedJobs.length > 0 && (
                                        <div className="mb-4">
                                            <button
                                                type="button"
                                                className="btn btn-link text-muted p-0 mb-2"
                                                onClick={() => setShowCompletedJobs(!showCompletedJobs)}
                                            >
                                                <i className={`bi ${showCompletedJobs ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
                                                Previously Completed Runs ({completedJobs.length})
                                            </button>
                                            {showCompletedJobs && (
                                                <div className="list-group">
                                                    {completedJobs.map(job => (
                                                        <div key={job.job_id} className="list-group-item d-flex justify-content-between align-items-center bg-light">
                                                            <div>
                                                                <span className="text-muted">
                                                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                                                    <strong>{job.total_files} files</strong> processed
                                                                </span>
                                                                <br />
                                                                <small className="text-muted">
                                                                    Completed: {new Date(job.created_at).toLocaleString()}
                                                                </small>
                                                            </div>
                                                            <span className="badge bg-secondary">Completed</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {scanError && (
                                        <div className="alert alert-danger mb-4">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            {scanError}
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        className="btn btn-primary btn-lg"
                                        onClick={handleScan}
                                        disabled={scanning}
                                    >
                                        {scanning ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Scanning...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-search me-2"></i>
                                                Start Scan
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* Step 2: Select Files */}
                            {step === 'select' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">
                                            <i className="bi bi-check2-square me-2"></i>
                                            Select Files to Reorganize ({scannedFiles.length} files found)
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={toggleSelectAll}
                                        >
                                            {selectAll ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>

                                    {/* Filters Component */}
                                    <BackfillFilters
                                        files={scannedFiles}
                                        filters={filters}
                                        onFiltersChange={setFilters}
                                    />

                                    {filteredFiles.length !== scannedFiles.length && (
                                        <div className="text-muted small mb-2">
                                            Showing {filteredFiles.length} of {scannedFiles.length} files
                                        </div>
                                    )}

                                    <div className="alert alert-info">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Select the files you want to reorganize. The AI will analyze them and suggest new names, folders, and summaries.
                                        {scannedFiles.some(f => f.inKnowledgebase) && (
                                            <span className="ms-2">
                                                <span className="badge bg-success">
                                                    <i className="bi bi-database-check me-1"></i>In KB
                                                </span>
                                                = already in knowledgebase
                                            </span>
                                        )}
                                    </div>

                                    {filteredFiles.length === 0 ? (
                                        <div className="text-center py-4 text-muted">
                                            {scannedFiles.length === 0 ? (
                                                'No files found in this client\'s folder.'
                                            ) : (
                                                'No files match your search.'
                                            )}
                                        </div>
                                    ) : (
                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            <table className="table table-hover table-sm">
                                                <thead className="table-light sticky-top">
                                                    <tr>
                                                        <th style={{ width: 40 }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectAll && selectedFileIds.size === filteredFiles.length}
                                                                onChange={toggleSelectAll}
                                                            />
                                                        </th>
                                                        <th
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleSort('name')}
                                                        >
                                                            Name{renderSortIcon('name')}
                                                        </th>
                                                        <th
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleSort('path')}
                                                        >
                                                            Path{renderSortIcon('path')}
                                                        </th>
                                                        <th
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleSort('size')}
                                                        >
                                                            Size{renderSortIcon('size')}
                                                        </th>
                                                        <th
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleSort('modified')}
                                                        >
                                                            Modified{renderSortIcon('modified')}
                                                        </th>
                                                        <th
                                                            style={{ width: 60, cursor: 'pointer' }}
                                                            onClick={() => handleSort('kb')}
                                                        >
                                                            KB{renderSortIcon('kb')}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedFiles.map(file => (
                                                        <tr
                                                            key={file.fileId}
                                                            className={selectedFileIds.has(file.fileId) ? 'table-primary' : ''}
                                                            onClick={() => toggleFileSelection(file.fileId)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <td onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={selectedFileIds.has(file.fileId)}
                                                                    onChange={() => toggleFileSelection(file.fileId)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <i className={`bi ${getFileIcon(file.name)} me-2`}></i>
                                                                {file.name}
                                                            </td>
                                                            <td title={file.folderPath}>
                                                                <small className="text-muted">{formatPathForDisplay(file.folderPath)}</small>
                                                            </td>
                                                            <td>{file.size ? formatBytes(file.size) : '-'}</td>
                                                            <td>
                                                                <small>{file.modifiedAt ? new Date(file.modifiedAt).toLocaleDateString() : '-'}</small>
                                                            </td>
                                                            <td className="text-center">
                                                                {file.inKnowledgebase ? (
                                                                    <span className="badge bg-success" title="Already in knowledgebase">
                                                                        <i className="bi bi-database-check"></i>
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <div className="mt-3 d-flex justify-content-between align-items-center">
                                        <div>
                                            {scannedFiles.some(f => f.inKnowledgebase) && (
                                                <small className="text-success">
                                                    <i className="bi bi-database-check me-1"></i>
                                                    {scannedFiles.filter(f => f.inKnowledgebase).length} of {scannedFiles.length} files already in knowledgebase
                                                </small>
                                            )}
                                        </div>
                                        <span className="text-muted">
                                            {selectedFileIds.size} file(s) selected
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Analyze */}
                            {step === 'analyze' && (
                                <div>
                                    <h5 className="mb-3">
                                        <i className="bi bi-cpu me-2"></i>
                                        Analyzing Files...
                                    </h5>

                                    {currentJobId && (
                                        <div className="alert alert-info mb-3">
                                            <i className="bi bi-info-circle me-2"></i>
                                            <strong>Job ID:</strong> {currentJobId.substring(0, 8)}...
                                            <br />
                                            <small className="text-muted">
                                                You can close this dialog and reopen it later to resume. The analysis will continue in the background.
                                            </small>
                                        </div>
                                    )}

                                    <div className="progress mb-4" style={{ height: 24 }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated"
                                            role="progressbar"
                                            style={{
                                                width: `${(Object.values(analysisJobs).filter(j => j.status === 'completed' || j.status === 'failed').length / Object.keys(analysisJobs).length) * 100}%`
                                            }}
                                        >
                                            {Object.values(analysisJobs).filter(j => j.status === 'completed' || j.status === 'failed').length} / {Object.keys(analysisJobs).length}
                                        </div>
                                    </div>

                                    <div className="list-group" style={{ maxHeight: 400, overflowY: 'auto' }}>
                                        {Object.entries(analysisJobs).map(([fileId, job]) => {
                                            const file = getFileInfo(fileId);
                                            return (
                                                <div key={fileId} className="list-group-item">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <i className={`bi ${getFileIcon(file?.name || job?.fileName || '')} me-2`}></i>
                                                            {file?.name || job?.fileName || 'Unknown file'}
                                                        </div>
                                                        <div>
                                                            {job.status === 'queued' && (
                                                                <span className="badge bg-light text-dark">Queued</span>
                                                            )}
                                                            {job.status === 'pending' && (
                                                                <span className="badge bg-secondary">
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                                    Pending
                                                                </span>
                                                            )}
                                                            {(job.status === 'analyzing' || job.status === 'processing') && (
                                                                <span className="badge bg-primary">
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                                    Analyzing
                                                                </span>
                                                            )}
                                                            {job.status === 'completed' && (
                                                                <span className="badge bg-success">
                                                                    <i className="bi bi-check me-1"></i>
                                                                    Complete
                                                                </span>
                                                            )}
                                                            {job.status === 'failed' && (
                                                                <span className="badge bg-danger">
                                                                    <i className="bi bi-x me-1"></i>
                                                                    Failed
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {job.status === 'completed' && job.result && (
                                                        <div className="mt-2 small text-muted">
                                                            <strong>Suggested:</strong> {job.result.suggestedName} → {job.result.suggestedFolder}
                                                        </div>
                                                    )}
                                                    {job.error && (
                                                        <div className="mt-2 small text-danger">{job.error}</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Review */}
                            {step === 'review' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">
                                            <i className="bi bi-pencil-square me-2"></i>
                                            Review & Edit Changes
                                        </h5>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-muted small">
                                                {reviewSelectedIds.size} of {Object.entries(fileDecisions).filter(([_, d]) => d.action !== 'skip').length} selected
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={toggleReviewSelectAll}
                                            >
                                                {Object.entries(fileDecisions)
                                                    .filter(([_, d]) => d.action !== 'skip')
                                                    .every(([id]) => reviewSelectedIds.has(id)) && reviewSelectedIds.size > 0
                                                    ? 'Deselect All' : 'Select All'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="alert alert-info">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Review the suggested changes below. Check the files you want to apply, then click "Apply Changes". You can edit the new name, select a different folder, or choose to skip or delete files.
                                    </div>

                                    <div className="accordion" id="reviewAccordion">
                                        {Object.entries(fileDecisions).map(([fileId, decision], idx) => {
                                            const file = getFileInfo(fileId);
                                            const job = analysisJobs[fileId];

                                            return (
                                                <div key={fileId} className="accordion-item">
                                                    <h2 className="accordion-header d-flex align-items-center">
                                                        {decision.action !== 'skip' && (
                                                            <div className="ms-2 d-flex align-items-center" style={{ minWidth: 24 }}>
                                                                <input
                                                                    type="checkbox"
                                                                    style={{ 
                                                                        width: 16, 
                                                                        height: 16, 
                                                                        margin: 0,
                                                                        cursor: 'pointer',
                                                                        accentColor: '#198754'
                                                                    }}
                                                                    checked={reviewSelectedIds.has(fileId)}
                                                                    onChange={() => toggleReviewSelection(fileId)}
                                                                />
                                                            </div>
                                                        )}
                                                        {decision.action === 'skip' && (
                                                            <div className="ms-2" style={{ minWidth: 24 }}></div>
                                                        )}
                                                        <button
                                                            className={`accordion-button ${idx !== 0 ? 'collapsed' : ''} flex-grow-1`}
                                                            type="button"
                                                            data-bs-toggle="collapse"
                                                            data-bs-target={`#collapse-${fileId}`}
                                                        >
                                                            <div className="d-flex align-items-center justify-content-between w-100 me-2">
                                                                <div className="d-flex align-items-center">
                                                                    <span className={`badge me-2 ${decision.action === 'delete' ? 'bg-danger' :
                                                                            decision.action === 'skip' ? 'bg-secondary' : 'bg-success'
                                                                        }`}>
                                                                        {decision.action === 'delete' ? 'Delete' :
                                                                            decision.action === 'skip' ? 'Skip' : 'Move'}
                                                                    </span>
                                                                    {file?.inKnowledgebase && (
                                                                        <span className="badge bg-info me-2" title="Already in knowledgebase">
                                                                            <i className="bi bi-database-check"></i> KB
                                                                        </span>
                                                                    )}
                                                                    <span className="text-truncate" style={{ maxWidth: 400 }}>
                                                                        {file?.name || job?.fileName || 'Unknown'}
                                                                    </span>
                                                                    {decision.action === 'move' && (
                                                                        <small className="text-muted ms-2">
                                                                            → {decision.targetFolder}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                                <div className="btn-group btn-group-sm" role="group" onClick={(e) => e.stopPropagation()}>
                                                                    <input
                                                                        type="radio"
                                                                        className="btn-check"
                                                                        name={`action-${fileId}`}
                                                                        id={`action-move-${fileId}`}
                                                                        checked={decision.action === 'move'}
                                                                        onChange={() => {
                                                                            updateDecision(fileId, { action: 'move' });
                                                                            // Expand the panel when Move is selected
                                                                            const collapseEl = document.getElementById(`collapse-${fileId}`);
                                                                            const accordionBtn = collapseEl?.previousElementSibling?.querySelector('.accordion-button');
                                                                            if (collapseEl && !collapseEl.classList.contains('show')) {
                                                                                collapseEl.classList.add('show');
                                                                                if (accordionBtn) accordionBtn.classList.remove('collapsed');
                                                                            }
                                                                        }}
                                                                    />
                                                                    <label className="btn btn-outline-success" htmlFor={`action-move-${fileId}`} title="Move & Rename">
                                                                        <i className="bi bi-folder-symlink"></i>
                                                                    </label>

                                                                    <input
                                                                        type="radio"
                                                                        className="btn-check"
                                                                        name={`action-${fileId}`}
                                                                        id={`action-delete-${fileId}`}
                                                                        checked={decision.action === 'delete'}
                                                                        onChange={() => {
                                                                            updateDecision(fileId, { action: 'delete' });
                                                                            // Collapse the panel when Delete is selected
                                                                            const collapseEl = document.getElementById(`collapse-${fileId}`);
                                                                            const accordionBtn = collapseEl?.previousElementSibling?.querySelector('.accordion-button');
                                                                            if (collapseEl && collapseEl.classList.contains('show')) {
                                                                                collapseEl.classList.remove('show');
                                                                                if (accordionBtn) accordionBtn.classList.add('collapsed');
                                                                            }
                                                                        }}
                                                                    />
                                                                    <label className="btn btn-outline-danger" htmlFor={`action-delete-${fileId}`} title="Delete">
                                                                        <i className="bi bi-trash"></i>
                                                                    </label>

                                                                    <input
                                                                        type="radio"
                                                                        className="btn-check"
                                                                        name={`action-${fileId}`}
                                                                        id={`action-skip-${fileId}`}
                                                                        checked={decision.action === 'skip'}
                                                                        onChange={() => {
                                                                            updateDecision(fileId, { action: 'skip' });
                                                                            // Collapse the panel when Skip is selected
                                                                            const collapseEl = document.getElementById(`collapse-${fileId}`);
                                                                            const accordionBtn = collapseEl?.previousElementSibling?.querySelector('.accordion-button');
                                                                            if (collapseEl && collapseEl.classList.contains('show')) {
                                                                                collapseEl.classList.remove('show');
                                                                                if (accordionBtn) accordionBtn.classList.add('collapsed');
                                                                            }
                                                                        }}
                                                                    />
                                                                    <label className="btn btn-outline-secondary" htmlFor={`action-skip-${fileId}`} title="Skip">
                                                                        <i className="bi bi-skip-forward"></i>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    </h2>
                                                    <div
                                                        id={`collapse-${fileId}`}
                                                        className={`accordion-collapse collapse ${idx === 0 ? 'show' : ''}`}
                                                        data-bs-parent="#reviewAccordion"
                                                    >
                                                        <div className="accordion-body">
                                                            {decision.action === 'skip' && (
                                                                <div className="text-center py-3 text-muted">
                                                                    <i className="bi bi-skip-forward fs-3 mb-2 d-block"></i>
                                                                    <p className="mb-0">This file will be skipped - no changes will be made.</p>
                                                                </div>
                                                            )}

                                                            {decision.action === 'delete' && (
                                                                <div className="text-center py-3 text-danger">
                                                                    <i className="bi bi-exclamation-triangle fs-3 mb-2 d-block"></i>
                                                                    <p className="mb-0"><strong>Warning:</strong> This file will be permanently deleted from SharePoint.</p>
                                                                </div>
                                                            )}

                                                            {decision.action === 'move' && (
                                                                <>
                                                                    {/* New Name */}
                                                                    <div className="mb-3">
                                                                        <label className="form-label fw-bold">New Filename</label>
                                                                        <div className="input-group">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={decision.newName}
                                                                                onChange={(e) => updateDecision(fileId, { newName: e.target.value })}
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary"
                                                                                title="Keep original filename"
                                                                                onClick={() => updateDecision(fileId, { newName: file?.name || job?.fileName || '' })}
                                                                            >
                                                                                <i className="bi bi-arrow-counterclockwise me-1"></i>
                                                                                Keep Original
                                                                            </button>
                                                                        </div>
                                                                        <small className="text-muted">Original: {file?.name || job?.fileName}</small>
                                                                    </div>

                                                                    {/* Target Folder */}
                                                                    <div className="mb-3">
                                                                        <label className="form-label fw-bold">Target Folder</label>
                                                                        <div className="input-group">
                                                                            <select
                                                                                className="form-select"
                                                                                style={{ maxWidth: '200px' }}
                                                                                value={folderOptions.some(f => f.name === decision.targetFolder?.replace(/\\/g, '/')) ? decision.targetFolder?.replace(/\\/g, '/') : ''}
                                                                                onChange={(e) => {
                                                                                    if (e.target.value) {
                                                                                        updateDecision(fileId, { targetFolder: e.target.value });
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <option value="">Custom...</option>
                                                                                {folderOptions.map(folder => (
                                                                                    folder.isSeparator
                                                                                        ? <option key={folder.id} value={folder.id} disabled>──────────────</option>
                                                                                        : <option key={folder.id} value={folder.name}>{folder.name}</option>
                                                                                ))}
                                                                            </select>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder="e.g., FP Docs/2024"
                                                                                value={decision.targetFolder?.replace(/\\/g, '/') || ''}
                                                                                onChange={(e) => updateDecision(fileId, { targetFolder: e.target.value.replace(/\\/g, '/') })}
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary"
                                                                                title="Keep original location"
                                                                                onClick={() => {
                                                                                    // Extract folder path from file's current location
                                                                                    // Path format: Client Documents/Clients X-Y/ClientName/SubFolder/file.pdf
                                                                                    // We want the subfolder part after the client folder (index 3+)
                                                                                    // Normalize backslashes to forward slashes first
                                                                                    const filePath = (file?.path || file?.folderPath || '').replace(/\\/g, '/');
                                                                                    const parts = filePath.split('/');
                                                                                    // Get everything after the client folder (parts[0-2]) and before the filename
                                                                                    const subfolderParts = parts.slice(3, -1);
                                                                                    const originalFolder = subfolderParts.length > 0 ? subfolderParts.join('/') : '';
                                                                                    updateDecision(fileId, { targetFolder: originalFolder || 'FP Docs' });
                                                                                }}
                                                                            >
                                                                                <i className="bi bi-arrow-counterclockwise me-1"></i>
                                                                                Keep Original
                                                                            </button>
                                                                        </div>
                                                                        <small className="text-muted">
                                                                            Select a folder or type a custom path (use / for subfolders)
                                                                        </small>
                                                                        {/* Show current location */}
                                                                        {file?.folderPath && (
                                                                            <div className="mt-1">
                                                                                <small className="text-muted">
                                                                                    <i className="bi bi-folder me-1"></i>
                                                                                    Current: {(() => {
                                                                                        const parts = file.folderPath.replace(/\\/g, '/').split('/');
                                                                                        return parts.slice(3).join('/') || '(root)';
                                                                                    })()}
                                                                                </small>
                                                                            </div>
                                                                        )}
                                                                        {/* Show folder description if a predefined folder is selected */}
                                                                        {(() => {
                                                                            const selectedFolder = folderOptions.find(f => f.name === decision.targetFolder?.replace(/\\/g, '/'));
                                                                            return selectedFolder?.description ? (
                                                                                <div className="mt-1">
                                                                                    <small className="text-info">
                                                                                        <i className="bi bi-info-circle me-1"></i>
                                                                                        {selectedFolder.description}
                                                                                    </small>
                                                                                </div>
                                                                            ) : null;
                                                                        })()}
                                                                    </div>

                                                                    {/* Document Classification Row */}
                                                                    <div className="row mb-3">
                                                                        {/* Doc Type */}
                                                                        <div className="col-md-4">
                                                                            <label className="form-label fw-bold">Doc Type</label>
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                value={decision.docTypeId || ''}
                                                                                onChange={(e) => {
                                                                                    const newDocTypeId = e.target.value;
                                                                                    // When doc type changes, clear sub type if it's not valid for the new doc type
                                                                                    const validSubTypes = getFilteredSubTypes(newDocTypeId);
                                                                                    const currentSubTypeValid = validSubTypes.some(st => st.id === decision.subTypeId);
                                                                                    updateDecision(fileId, { 
                                                                                        docTypeId: newDocTypeId,
                                                                                        subTypeId: currentSubTypeValid ? decision.subTypeId : ''
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <option value="">Select Type...</option>
                                                                                {docTypesData.map(dt => (
                                                                                    <option key={dt.id || dt.name} value={dt.id || dt.name}>
                                                                                        {dt.name}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </div>

                                                                        {/* Sub Type */}
                                                                        <div className="col-md-4">
                                                                            <label className="form-label fw-bold">Sub Type</label>
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                value={decision.subTypeId || ''}
                                                                                onChange={(e) => updateDecision(fileId, { subTypeId: e.target.value })}
                                                                            >
                                                                                <option value="">Select Sub-Type...</option>
                                                                                {getFilteredSubTypes(decision.docTypeId).map(st => (
                                                                                    <option key={st.id || st.name} value={st.id || st.name}>
                                                                                        {st.name}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </div>

                                                                        {/* Account Number */}
                                                                        <div className="col-md-4">
                                                                            <label className="form-label fw-bold">Account Number</label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="e.g., x1234, Mixed, NONE"
                                                                                value={decision.accountNumber || ''}
                                                                                onChange={(e) => updateDecision(fileId, { accountNumber: e.target.value })}
                                                                            />
                                                                            <small className="text-muted">
                                                                                x1234 (last 4 digits), Mixed (multiple), NONE
                                                                            </small>
                                                                        </div>
                                                                    </div>

                                                                    {/* Summary Preview - Collapsible */}
                                                                    {decision.summary && (
                                                                        <div className="mb-3">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    const summaryDiv = e.currentTarget.nextElementSibling;
                                                                                    const icon = e.currentTarget.querySelector('i');
                                                                                    if (summaryDiv.style.display === 'none') {
                                                                                        summaryDiv.style.display = 'block';
                                                                                        icon.classList.remove('bi-chevron-right');
                                                                                        icon.classList.add('bi-chevron-down');
                                                                                    } else {
                                                                                        summaryDiv.style.display = 'none';
                                                                                        icon.classList.remove('bi-chevron-down');
                                                                                        icon.classList.add('bi-chevron-right');
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <i className="bi bi-chevron-right me-1"></i>
                                                                                <span className="form-label fw-bold mb-0">AI Summary</span>
                                                                            </button>
                                                                            <div className="border rounded p-2 bg-light small markdown-content" style={{ display: 'none', maxHeight: '400px', overflowY: 'auto' }}>
                                                                                <ReactMarkdown>
                                                                                    {decision.summary}
                                                                                </ReactMarkdown>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Step 5: Apply */}
                            {step === 'apply' && (
                                <div>
                                    <h5 className="mb-3">
                                        <i className="bi bi-gear me-2"></i>
                                        {applying ? 'Applying Changes...' : 'Complete'}
                                    </h5>

                                    {applying && (
                                        <div className="progress mb-4" style={{ height: 24 }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                role="progressbar"
                                                style={{ width: `${(applyProgress.current / applyProgress.total) * 100}%` }}
                                            >
                                                {applyProgress.current} / {applyProgress.total}
                                            </div>
                                        </div>
                                    )}

                                    {applyResults.length > 0 && (
                                        <div className="list-group">
                                            {applyResults.map((result, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`list-group-item ${result.success ? 'list-group-item-success' : 'list-group-item-danger'}`}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <i className={`bi ${result.success ? 'bi-check-circle' : 'bi-x-circle'} me-2`}></i>
                                                            {result.fileName}
                                                        </div>
                                                        <span className={`badge ${result.action === 'delete' ? 'bg-danger' : 'bg-success'}`}>
                                                            {result.action === 'delete' ? 'Deleted' : 'Moved'}
                                                        </span>
                                                    </div>
                                                    {result.newPath && (
                                                        <small className="text-muted">→ {result.newPath}</small>
                                                    )}
                                                    {result.error && (
                                                        <small className="text-danger d-block">{result.error}</small>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!applying && applyResults.length > 0 && (
                                        <div className={`alert ${Object.keys(fileDecisions).length > 0 ? 'alert-info' : 'alert-success'} mt-4`}>
                                            <i className={`bi ${Object.keys(fileDecisions).length > 0 ? 'bi-info-circle' : 'bi-check-circle'} me-2`}></i>
                                            <strong>{applyResults.filter(r => r.success).length} of {applyResults.length} files processed successfully.</strong>
                                            {Object.keys(fileDecisions).length > 0 && (
                                                <span className="ms-2">
                                                    {Object.keys(fileDecisions).length} file(s) remaining to review.
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            {step === 'scan' && (
                                <button type="button" className="btn btn-secondary" onClick={onClose}>
                                    Cancel
                                </button>
                            )}

                            {step === 'select' && (
                                <>
                                    <button type="button" className="btn btn-secondary" onClick={() => setStep('scan')}>
                                        <i className="bi bi-arrow-left me-1"></i>
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleAnalyze}
                                        disabled={selectedFileIds.size === 0}
                                    >
                                        <i className="bi bi-cpu me-1"></i>
                                        Analyze Selected ({selectedFileIds.size})
                                    </button>
                                </>
                            )}

                            {step === 'analyze' && (
                                <button type="button" className="btn btn-secondary" onClick={onClose}>
                                    <i className="bi bi-x-lg me-1"></i>
                                    Close (Analysis Continues)
                                </button>
                            )}

                            {step === 'review' && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setStep('select')}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i>
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={handleApply}
                                        disabled={reviewSelectedIds.size === 0}
                                    >
                                        <i className="bi bi-check-all me-1"></i>
                                        Apply Changes ({reviewSelectedIds.size})
                                    </button>
                                </>
                            )}

                            {step === 'apply' && !applying && (
                                <>
                                    {Object.keys(fileDecisions).length > 0 ? (
                                        // Still have files to review
                                        <button 
                                            type="button" 
                                            className="btn btn-primary" 
                                            onClick={() => setStep('review')}
                                        >
                                            <i className="bi bi-arrow-right me-1"></i>
                                            Continue with Remaining ({Object.keys(fileDecisions).length})
                                        </button>
                                    ) : (
                                        // All files processed
                                        <button type="button" className="btn btn-success" onClick={onClose}>
                                            <i className="bi bi-check me-1"></i>
                                            Done - All Files Processed
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
}

/**
 * Get appropriate icon for file based on extension.
 */
function getFileIcon(filename) {
    if (!filename) return 'bi-file';
    const ext = filename.split('.').pop()?.toLowerCase();

    switch (ext) {
        case 'pdf':
            return 'bi-file-earmark-pdf text-danger';
        case 'doc':
        case 'docx':
            return 'bi-file-earmark-word text-primary';
        case 'xls':
        case 'xlsx':
            return 'bi-file-earmark-excel text-success';
        case 'ppt':
        case 'pptx':
            return 'bi-file-earmark-ppt text-warning';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
            return 'bi-file-earmark-image text-info';
        case 'txt':
            return 'bi-file-earmark-text';
        case 'zip':
        case 'rar':
            return 'bi-file-earmark-zip';
        default:
            return 'bi-file-earmark';
    }
}
